import { Link } from 'react-router-dom';
import { MapPin, Award, Clock, Phone, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getWhatsAppLink, getCategoryColor } from '../../lib/utils';

export const DoctorCard = ({ doctor, featured = false }) => {
  const primarySpeciality = doctor.speciality_details?.[0];
  const categoryColor = primarySpeciality ? getCategoryColor(primarySpeciality.category) : null;

  return (
    <div
      className={`doctor-card bg-white ${featured ? 'md:col-span-2' : ''}`}
      data-testid={`doctor-card-${doctor.id}`}
    >
      <div className={`relative ${featured ? 'md:flex' : ''}`}>
        {/* Image */}
        <div className={`relative overflow-hidden ${featured ? 'md:w-2/5' : ''}`}>
          <div className={`aspect-[4/3] ${featured ? 'md:aspect-square md:h-full' : ''}`}>
            <img
              src={doctor.photo_url || 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400'}
              alt={doctor.name}
              className="w-full h-full object-cover"
            />
          </div>
          {doctor.is_verified && (
            <div className="absolute top-3 left-3 verified-badge flex items-center gap-1">
              <BadgeCheck className="w-3.5 h-3.5" />
              Verified
            </div>
          )}
          {doctor.is_featured && (
            <div className="absolute top-3 right-3 bg-navy-800 text-white text-xs px-3 py-1 rounded-full font-medium">
              Featured
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`p-5 ${featured ? 'md:w-3/5 md:p-6' : ''}`}>
          <Link to={`/doctor/${doctor.slug}`}>
            <h3 className="font-heading font-semibold text-lg text-navy-900 hover:text-navy-700 transition-colors">
              {doctor.name}
            </h3>
          </Link>

          {/* Specialities */}
          <div className="flex flex-wrap gap-2 mt-2">
            {doctor.speciality_details?.slice(0, 3).map((spec) => (
              <Badge
                key={spec.id}
                variant="secondary"
                className={`text-xs ${getCategoryColor(spec.category).bg} ${getCategoryColor(spec.category).text}`}
              >
                {spec.name}
              </Badge>
            ))}
          </div>

          {/* Location & Experience */}
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
            {doctor.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-gray-400" />
                {doctor.location.city}
              </span>
            )}
            {doctor.experience_years > 0 && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-gray-400" />
                {doctor.experience_years} years exp
              </span>
            )}
          </div>

          {/* Bio */}
          {featured && doctor.bio && (
            <p className="mt-3 text-sm text-gray-600 line-clamp-2">
              {doctor.bio}
            </p>
          )}

          {/* Clinic */}
          {doctor.clinic_name && (
            <p className="mt-3 text-sm text-gray-500">
              {doctor.clinic_name}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 mt-4">
            <Link to={`/doctor/${doctor.slug}`} className="flex-1">
              <Button
                variant="outline"
                className="w-full rounded-full border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white text-sm"
              >
                View Profile
              </Button>
            </Link>
            <a
              href={getWhatsAppLink(`Hi, I'd like to book an appointment with ${doctor.name}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white px-4">
                <Phone className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
